import React from "react";
import {
  Compass, Palette, PenTool, BookOpen, Clapperboard,
  Share2, MonitorSmartphone, Presentation, Layers, Sparkles,
} from "lucide-react";
import { Reveal, StaggerGroup, StaggerItem } from "./Reveal";

const SERVICES = [
  { icon: Compass, title: "Brand Strategy", desc: "Positioning, messaging and market clarity that give your brand a reason to exist." },
  { icon: Palette, title: "Visual Identity", desc: "Cohesive systems — colour, type and art direction — that feel unmistakably yours." },
  { icon: PenTool, title: "Logo Design", desc: "Distinctive, timeless marks built to scale across every touchpoint." },
  { icon: BookOpen, title: "Brand Guidelines", desc: "Clear, usable rulebooks so your brand stays consistent everywhere." },
  { icon: Clapperboard, title: "Creative Direction", desc: "A guiding vision that keeps campaigns and content on-message and on-brand." },
  { icon: Share2, title: "Social Media Design", desc: "Scroll-stopping template systems that grow audience and engagement." },
  { icon: MonitorSmartphone, title: "Website & Landing Pages", desc: "High-converting, beautifully crafted pages that turn visitors into customers." },
  { icon: Presentation, title: "Presentation Design", desc: "Investor and sales decks engineered to win rooms and close deals." },
  { icon: Layers, title: "Marketing Collateral", desc: "Brochures, decks and print that carry your brand with confidence." },
  { icon: Sparkles, title: "AI Creative Solutions", desc: "AI-powered workflows to explore more directions and ship premium work faster." },
];

export const Services = () => {
  return (
    <section id="services" className="py-24 md:py-32 lg:py-40 bg-cream" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-3xl">
          <Reveal>
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-gold" />
              <span className="text-xs tracking-[0.25em] uppercase font-semibold text-gold">Services</span>
            </div>
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-charcoal">
              A full studio, <span className="italic text-burgundy">one partner.</span>
            </h2>
            <p className="mt-6 text-base md:text-lg text-charcoal/70 leading-relaxed">
              Everything you need to build, launch and grow a brand that means business —
              from first strategy session to ongoing creative partnership.
            </p>
          </Reveal>
        </div>

        <StaggerGroup className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-charcoal/10 rounded-3xl overflow-hidden border border-charcoal/10" stagger={0.05}>
          {SERVICES.map((s) => {
            const Icon = s.icon;
            return (
              <StaggerItem key={s.title}>
                <div className="group h-full bg-cream hover:bg-white p-8 md:p-10 transition-colors duration-500 cursor-hover" data-testid={`service-card-${s.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  <div className="flex h-12 w-12 items-center justify-center rounded-full border border-charcoal/10 text-charcoal group-hover:bg-burgundy group-hover:text-cream group-hover:border-burgundy transition-colors duration-500">
                    <Icon size={20} strokeWidth={1.6} />
                  </div>
                  <h3 className="mt-6 font-heading text-2xl text-charcoal">{s.title}</h3>
                  <p className="mt-3 text-sm md:text-base text-charcoal/60 leading-relaxed">{s.desc}</p>
                </div>
              </StaggerItem>
            );
          })}
        </StaggerGroup>
      </div>
    </section>
  );
};
